import React, { useState } from 'react';

const ViatorSupplierHighlight = () => {
  const [activeTab, setActiveTab] = useState('about');
  const [billingCycle, setBillingCycle] = useState(1); // 1 = Monthly, 2 = Annually

  // Company Information - Zotrai (YOUR COMPANY)
  const companyInfo = {
    name: "Zotrai",
    themeColor: "#FF7F00",
    description: "Your trusted B2B travel technology partner",
    founded: "2020",
    headquarters: "India",
    agents: "10,000+",
    suppliers: "50+",
    countries: "100+",
    uptime: "99.9%",
    apiCalls: "100M+",
    team: "100+",
    email: "support@zotrai.com",
    phone: "+91-7207642485"
  };

  // Why Suppliers Choose Zotrai - REAL DATA
  const whyZotraiData = {
    benefits: [
        {
        id: 1,
        title: "Single API Integration, Multiple Connections",
        description: "Connect once with Zotrai and get instant access to all our partner suppliers - no multiple integrations needed",
        icon: "ri-plug-line",
        stat: "1",
        statLabel: "Integration"
      },
     {
  id: 2,
  title: "Launch B2B, B2C & Corporate Portals",
  description: "Fully customizable multi-agent, direct-sell, and corporate booking platforms under your brand",
  icon: "ri-window-line",
  stat: "All-in-One",
  statLabel: "Portal Suite"
},
    
      {
  id: 3,
  title: "Save 95% on Integration Costs",
  description: "No setup fees, no integration charges.",
icon: "ri-coins-line",

  stat: "95%",
  statLabel: "Cost Savings"
},
      {
        id: 4,
        title: "Real-time Analytics Dashboard",
        description: "Track your inventory performance, booking patterns, and agent behavior with detailed analytics",
        icon: "ri-bar-chart-2-line",
        stat: "24/7",
        statLabel: "Monitoring"
      },
      {
        id: 5,
        title: "99.9% Uptime Guarantee",
        description: "Enterprise-grade infrastructure with redundant systems ensuring your APIs are always available",
        icon: "ri-shield-check-line",
        stat: "99.9%",
        statLabel: "Uptime"
      },
      {
        id: 6,
        title: "Dedicated Account Manager",
        description: "Every supplier gets a dedicated technical account manager for seamless integration and support",
        icon: "ri-customer-service-line",
        stat: "24/7",
        statLabel: "Support"
      }
    ],
    process: [
      {
        step: 1,
        title: "Add Your API Credentials",
        description: "Just Login, paste your API keys, and we handle the rest – zero development effort"
      },
       {
        step: 2,
        title: "Select & Customize Template",
        description: "Pick from our library of 100+ responsive templates and match your brand identity"
      },
      {
        step: 3,
        title: "Go Live Instantly",
        description: "Deploy to production in one click and start accepting real bookings"
      }
    ]
  };

  // SEO-friendly structured data for Viator
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Viator",
    "description": "World's largest marketplace for travel experiences, tours, and activities with instant booking confirmation",
    "url": "https://www.viator.com",
    "areaServed": "Worldwide",
    "serviceType": ["Tours & Activities", "Skip-the-line Tickets", "Local Experiences", "Attractions"],
    "parentOrganization": {
      "@type": "Organization",
      "name": "Tripadvisor"
    }
  };

  // Viator specific data based on search results [citation:2][citation:4][citation:7]
  const viatorData = {
    name: "Viator",
    description: "World's largest marketplace for travel experiences, tours, and activities with instant booking confirmation. A Tripadvisor company.",
    longDescription: "Viator is the world's leading online marketplace for booking tours, activities, and attractions at destinations worldwide. As a Tripadvisor company, Viator connects travelers with over 300,000+ experiences across 200+ countries, ranging from guided city walking tours and sunset cruises to adventurous excursions, cooking classes, and cultural activities. Founded in 1995, Viator has revolutionized the way travelers discover and book experiences, offering skip-the-line access to major attractions, local guides, and mobile ticketing. Their comprehensive API allows partners to access real-time availability, dynamic pricing, and instant booking confirmation, making them the preferred choice for travel agents and tour operators seeking to offer their clients unforgettable experiences.",
    founded: "1995",
    parentCompany: "Tripadvisor",
    parentSince: "2014",
    headquarters: "London, UK",
    employees: "1000+",
    countries: "200+",
    experiences: "300,000+",
    tours: "200,000+",
    attractions: "50,000+",
    activities: "300,000+",
    apiCalls: "500M+",
    agents: "15,000+",
    themeColor: "#E31B23", // Viator red color
    website: "https://www.viator.com",
    apiType: "RESTful API (v2.0)",
    integrationType: "Partner API / Supplier API",
    support: "24/7 Global Support",
    certifications: ["Tripadvisor Verified", "PCI Compliant", "ISO 27001"]
  };

  // Key features of Viator
  const features = [
    {
      icon: "🎫",
      title: "300,000+ Experiences",
      description: "World's largest collection of tours, activities, and attractions across 200+ countries"
    },
    {
      icon: "⏱️",
      title: "Skip-the-line Access",
      description: "Priority access to major attractions and museums worldwide"
    },
    {
      icon: "📱",
      title: "Mobile Ticketing",
      description: "Instant mobile tickets with QR code scanning for seamless entry"
    },
    {
      icon: "🌍",
      title: "Global Coverage",
      description: "Experiences in 200+ countries with local guides and authentic activities"
    }
  ];

  // Services offered by Viator based on API documentation [citation:4][citation:7]
  const services = [
    {
      id: 1,
      title: "Tours & Activities API",
      description: "Access to 300,000+ tours, activities, and experiences worldwide with real-time availability and pricing.",
      features: [
        "Real-time availability",
        "Dynamic pricing",
        "Multi-language content",
        "High-resolution images",
        "Customer reviews",
        "Instant confirmation"
      ],
      icon: "🎫"
    },
    {
      id: 2,
      title: "Booking Management API",
      description: "Comprehensive booking lifecycle management with hold, book, cancel, and amend functionality.",
      features: [
        "Booking hold/reserve",
        "Instant booking",
        "Cancellation requests",
        "Booking amendments",
        "Status updates",
        "Refund processing"
      ],
      icon: "📅"
    },
    {
      id: 3,
      title: "Availability & Pricing API",
      description: "Real-time availability checking and pricing calculations for all product options and traveler types.",
      features: [
        "Real-time availability",
        "Per-person pricing",
        "Tiered pricing",
        "Special offers",
        "Last-minute deals",
        "Group rates"
      ],
      icon: "💰"
    },
    {
      id: 4,
      title: "Product Content API",
      description: "Rich product content including descriptions, images, reviews, and detailed itineraries.",
      features: [
        "Product descriptions",
        "Photo galleries",
        "Video content",
        "Reviews & ratings",
        "Terms & conditions",
        "Meeting points"
      ],
      icon: "📋"
    },
    {
      id: 5,
      title: "Attraction Tickets",
      description: "Skip-the-line tickets to major attractions, museums, and landmarks worldwide.",
      features: [
        "Priority access",
        "Mobile tickets",
        "Audio guides",
        "Combo packages",
        "Guided tours",
        "Fast-track entry"
      ],
      icon: "🏛️"
    },
    {
      id: 6,
      title: "Local Experiences",
      description: "Curated local experiences including cooking classes, cultural workshops, and unique activities.",
      features: [
        "Cooking classes",
        "Cultural workshops",
        "Photography tours",
        "Wine tasting",
        "Art classes",
        "Local guides"
      ],
      icon: "🍳"
    }
  ];

  // Technology highlights based on API docs [citation:1][citation:4]
  const technology = [
    {
      title: "RESTful API v2.0",
      description: "Modern RESTful API architecture with comprehensive documentation and versioning"
    },
    {
      title: "Real-time Availability",
      description: "Live inventory checks with millisecond response times"
    },
    {
      title: "Webhook Notifications",
      description: "Real-time updates for availability, pricing, and booking status changes"
    },
    {
      title: "Exchange Rate API",
      description: "Dynamic currency conversion with real-time exchange rates"
    },
    {
      title: "Contract Testing",
      description: "Automated contract testing tools for API validation"
    },
    {
      title: "Rate Limiting",
      description: "Intelligent rate limiting with configurable thresholds"
    }
  ];

  // Integration process based on certification requirements [citation:5]
  const integrationProcess = [
    {
      step: 1,
      title: "API Credentials",
      description: "Receive API key and supplier credentials after partner registration"
    },
    {
      step: 2,
      title: "Sandbox Testing",
      description: "Test integration in Viator's staging environment with certification"
    },
    {
      step: 3,
      title: "Go Live",
      description: "Deploy to production with 24/7 global support"
    }
  ];

  // Why choose Viator
  const whyChoose = [
    "Part of Tripadvisor family with global reach and trust",
    "300,000+ experiences across 200+ countries",
    "Skip-the-line access to major attractions",
    "Real-time availability and instant confirmation",
    "Multi-language and multi-currency support",
    "24/7 global customer support"
  ];

  // Supplier logos array with navigation paths
  const suppliers = [
    { 
      id: 1, 
      name: "Tripjack", 
      logo: "/assets/images/suppliers/tripjack.png",
      path: "/supplier/trip-jack"
    },
    { 
      id: 2, 
      name: "TBO", 
      logo: "/assets/images/suppliers/tbo.png",
      path: "/supplier/tbo"
    },
    { 
      id: 3, 
      name: "Akbar Travels", 
      logo: "/assets/images/suppliers/akbar.png",
      path: "/supplier/akbar-travels"
    },
    { 
      id: 4, 
      name: "RezLive", 
      logo: "/assets/images/suppliers/rezlive.png",
      path: "/supplier/rezlive"
    },
    { 
      id: 5, 
      name: "GRN Connect", 
      logo: "/assets/images/suppliers/grn.png",
      path: "/supplier/grn-connect"
    },
    { 
      id: 6, 
      name: "Riya Travel", 
      logo: "/assets/images/suppliers/riya.png",
      path: "/supplier/riya-travel"
    },
    { 
      id: 7, 
      name: "Viator", 
      logo: "/assets/images/suppliers/viator.png",
      path: "/supplier/viator"
    },
    { 
      id: 8, 
      name: "Air iQ", 
      logo: "/assets/images/suppliers/airiq.png",
      path: "/supplier/air-iq"
    }
  ];

  // Pricing plans data
  const plans = [
    {
      id: 1,
      name: "Starter",
      description: "🚀 All-in-one powerful plan with complete access to every feature.",
      monthly: 2999,
      annually: 29999,
      popular: true,
      emoji: "🚀",
      features: [
        "Search, compare, and book flights from top global suppliers in one place",
        "Access and connect with 1,000+ verified global DMCs",
        "Smart accounting suite with ledgers, invoices, supplier settlements, and corporate payments",
        "Capture leads directly from social media and run promotions from a single dashboard",
        "Generate professional itineraries in minutes using AI",
        "AI-powered visa processing and document management system",
        "One unified platform for Flights, Hotels, Visas, Cars, Sightseeing, and more",
        "Integrated and secure payment gateway",
        "Plug in and manage your own supplier APIs seamlessly",
        "AI-based WhatsApp automation for leads, follow-ups, and customer support",
        "Unlimited email marketing and promotional campaigns",
        "Advanced reports and analytics for sales, revenue, and performance tracking",
      ],
      addons: [
        "Launch your own B2C booking website for customers in minutes – just INR 999",
        "Launch a full-featured B2C travel portal with flights, hotels, and packages using your preferred suppliers – INR 1,999",
        "Launch a B2B travel portal for your agent network with flights, hotels, and packages – INR 1,999",
        "Launch a B2E corporate travel portal for your business clients with flights, hotels, and packages – INR 1,999",
      ],
    },
  ];

  const getAccentColor = (planName) => {
    if (planName === "Starter") return "#10B981";
    if (planName === "Pro") return "#3B82F6";
    return "#8B5CF6";
  };

  const handleEmailClick = () => {
    window.location.href = `mailto:${companyInfo.email}`;
  };

  const handlePhoneClick = () => {
    window.location.href = `tel:${companyInfo.phone.replace(/[^0-9+]/g, '')}`;
  };

  const handleSupplierClick = (path) => {
    window.location.href = path;
  };

  return (
    <>
      {/* SEO Meta Tags */}
      <Helmet>
        <title>Viator - World's Largest Experiences Marketplace | Zotrai</title>
        <meta name="description" content="Viator is the world's leading marketplace for tours, activities, and attractions with 300,000+ experiences across 200+ countries. A Tripadvisor company." />
        <meta name="keywords" content="Viator supplier, tours and activities, travel experiences, attraction tickets, skip-the-line, local guides" />
        <link rel="canonical" href="https://yourcompany.com/supplier/viator" />
        
        {/* Open Graph tags */}
        <meta property="og:title" content="Viator - World's Largest Experiences Marketplace" />
        <meta property="og:description" content="Connect with Viator's global network of 300,000+ tours and activities through Zotrai's single API integration." />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      {/* Main Content with viator- prefix */}
      <div className="viator-supplier-container">
        {/* Header Section - Viator Hero */}
        <header className="viator-header">
          <div className="viator-container">
            <h1 className="viator-title">
              <span className="viator-highlight" style={{ color: viatorData.themeColor }}>Viator</span>
              <br />
              World's Largest Experiences Marketplace
            </h1>
            <p className="viator-subtitle">
              {viatorData.description}
            </p>
          </div>
        </header>

        {/* Tabs Navigation - Same as others */}
        <div className="viator-tabs-container">
          <div className="viator-container">
            <div className="viator-tabs">
              <button 
                className={`viator-tab ${activeTab === 'about' ? 'viator-tab-active' : ''}`}
                onClick={() => setActiveTab('about')}
              >
                About
              </button>
              <button 
                className={`viator-tab ${activeTab === 'services' ? 'viator-tab-active' : ''}`}
                onClick={() => setActiveTab('services')}
              >
                Services
              </button>
              <button 
                className={`viator-tab ${activeTab === 'technology' ? 'viator-tab-active' : ''}`}
                onClick={() => setActiveTab('technology')}
              >
                Technology
              </button>
              <button 
                className={`viator-tab ${activeTab === 'whyzotrai' ? 'viator-tab-active' : ''}`}
                onClick={() => setActiveTab('whyzotrai')}
              >
                Why Zotrai <span className="viator-tab-badge">For Suppliers</span>
              </button>
              <button 
                className={`viator-tab ${activeTab === 'suppliers' ? 'viator-tab-active' : ''}`}
                onClick={() => setActiveTab('suppliers')}
              >
                Suppliers
              </button>
              <button 
                className={`viator-tab ${activeTab === 'pricing' ? 'viator-tab-active' : ''}`}
                onClick={() => setActiveTab('pricing')}
              >
                Pricing
              </button>
            </div>
          </div>
        </div>

        {/* Tab Content */}
        <div className="viator-tab-content">
          {activeTab === 'about' && (
            <section className="viator-about">
              <div className="viator-container">
                <div className="viator-grid">
                  <div className="viator-about-content">
                    <h2 className="viator-section-title">About <span className="viator-accent" style={{ color: viatorData.themeColor }}>Viator</span></h2>
                    <p className="viator-text">
                      {viatorData.longDescription}
                    </p>
                    
                    <div className="viator-features">
                      {features.map((feature, index) => (
                        <div key={index} className="viator-feature">
                          <span className="viator-feature-icon">{feature.icon}</span>
                          <div>
                            <h4>{feature.title}</h4>
                            <p>{feature.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  
                  <div className="viator-card">
                    <h3 className="viator-card-title">Viator at a Glance</h3>
                    <ul className="viator-list">
                      <li className="viator-list-item">
                        <span className="viator-check" style={{ color: viatorData.themeColor }}>✓</span>
                        Founded 1995 • Part of Tripadvisor since 2014
                      </li>
                      <li className="viator-list-item">
                        <span className="viator-check" style={{ color: viatorData.themeColor }}>✓</span>
                        300,000+ Tours & Activities
                      </li>
                      <li className="viator-list-item">
                        <span className="viator-check" style={{ color: viatorData.themeColor }}>✓</span>
                        200+ Countries Worldwide
                      </li>
                      <li className="viator-list-item">
                        <span className="viator-check" style={{ color: viatorData.themeColor }}>✓</span>
                        50,000+ Attractions
                      </li>
                      <li className="viator-list-item">
                        <span className="viator-check" style={{ color: viatorData.themeColor }}>✓</span>
                        Skip-the-line Access
                      </li>
                      <li className="viator-list-item">
                        <span className="viator-check" style={{ color: viatorData.themeColor }}>✓</span>
                        Mobile Ticketing
                      </li>
                      <li className="viator-list-item">
                        <span className="viator-check" style={{ color: viatorData.themeColor }}>✓</span>
                        Real-time Availability
                      </li>
                      <li className="viator-list-item">
                        <span className="viator-check" style={{ color: viatorData.themeColor }}>✓</span>
                        24/7 Global Support
                      </li>
                    </ul>

                    <h3 className="viator-card-title viator-mt-4">Certifications</h3>
                    <div className="viator-certification-tags">
                      {viatorData.certifications.map((cert, index) => (
                        <span key={index} className="viator-cert-tag">{cert}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </section>
          )}

          {activeTab === 'services' && (
            <section className="viator-services">
              <div className="viator-container">
                <h2 className="viator-section-title viator-text-center">
                  Comprehensive Experiences Marketplace
                </h2>
                <p className="viator-section-subtitle">
                  Through our integration with Viator, we deliver end-to-end tours and activities solutions
                </p>

                <div className="viator-services-grid">
                  {services.map((service) => (
                    <div key={service.id} className="viator-service-card">
                      <div className="viator-service-icon">{service.icon}</div>
                      <h3 className="viator-service-title">{service.title}</h3>
                      <p className="viator-service-description">{service.description}</p>
                      <ul className="viator-service-list">
                        {service.features.map((feature, index) => (
                          <li key={index} className="viator-service-list-item">
                            <span className="viator-dot" style={{ color: viatorData.themeColor }}>•</span>
                            {feature}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}

          {activeTab === 'technology' && (
            <section className="viator-technology">
              <div className="viator-container">
                <div className="viator-grid">
                  <div className="viator-integration-box">
                    <h3 className="viator-card-title">Seamless Integration</h3>
                    <p className="viator-text">
                      Our integration with Viator's robust v2.0 APIs ensures smooth data flow, real-time availability, and reliable performance for tours and activities bookings.
                    </p>
                    <div className="viator-tags">
                      {technology.map((tech, index) => (
                        <span key={index} className="viator-tag">{tech.title}</span>
                      ))}
                    </div>
                  </div>

                  <div className="viator-benefits-box">
                    <h3 className="viator-card-title">Key Advantages</h3>
                    <ul className="viator-benefits-list">
                      <li className="viator-benefits-item">
                        <span className="viator-benefits-icon">⚡</span>
                        Lightning-fast API response times
                      </li>
                      <li className="viator-benefits-item">
                        <span className="viator-benefits-icon">🛡️</span>
                        Enterprise-grade security protocols
                      </li>
                      <li className="viator-benefits-item">
                        <span className="viator-benefits-icon">🎧</span>
                        24/7 global technical support
                      </li>
                      <li className="viator-benefits-item">
                        <span className="viator-benefits-icon">👥</span>
                        Experienced integration team
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>
          )}

          {activeTab === 'whyzotrai' && (
            <section className="viator-why-zotrai-section">
              <div className="viator-container">
                {/* Header */}
                <div className="viator-why-header">
                  <span className="viator-why-badge" style={{ background: `${companyInfo.themeColor}15`, color: companyInfo.themeColor }}>
                    <i className="ri-question-line"></i> WHY SUPPLIERS CHOOSE ZOTRAI
                  </span>
                  <h2 className="viator-section-title">
                    Your Growth Partner in <span className="viator-accent" style={{ color: companyInfo.themeColor }}>Travel Distribution</span>
                  </h2>
                  <p className="viator-section-subtitle">
                    Join 50+ leading travel suppliers who trust us to connect them with travel agents
                  </p>
                </div>

                {/* Benefits Grid */}
                <div className="viator-benefits-grid-full">
                  {whyZotraiData.benefits.map((benefit) => (
                    <div key={benefit.id} className="viator-benefit-card">
                      <div className="viator-benefit-icon-wrapper" style={{ background: `${companyInfo.themeColor}15` }}>
                        <i className={benefit.icon} style={{ color: companyInfo.themeColor }}></i>
                      </div>
                      <div className="viator-benefit-content">
                        <h3>{benefit.title}</h3>
                        <p>{benefit.description}</p>
                        <div className="viator-benefit-stat">
                          <span className="viator-stat-highlight" style={{ color: companyInfo.themeColor }}>{benefit.stat}</span>
                          <span className="viator-stat-label">{benefit.statLabel}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Integration Process */}
                <div className="viator-integration-process">
                  <h3 className="viator-process-title">Simple 3-Step Integration</h3>
                  <div className="viator-process-steps">
                    {whyZotraiData.process.map((step) => (
                      <div key={step.step} className="viator-process-step">
                        <div className="viator-step-number" style={{ background: companyInfo.themeColor }}>{step.step}</div>
                        <h4>{step.title}</h4>
                        <p>{step.description}</p>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Contact Section */}
                <div className="viator-contact-section">
                  <h3>Ready to Join Our Supplier Network?</h3>
                  <p>Connect with agents through a single integration</p>
                  
                  <div className="viator-contact-info">
                    <div className="viator-contact-item" onClick={handleEmailClick}>
                      <div className="viator-contact-icon" style={{ backgroundColor: `${companyInfo.themeColor}15` }}>
                        <i className="ri-mail-line" style={{ color: companyInfo.themeColor }}></i>
                      </div>
                      <div className="viator-contact-details">
                        <span className="viator-contact-label">Email Support</span>
                        <span className="viator-contact-value">{companyInfo.email}</span>
                      </div>
                    </div>

                    <div className="viator-contact-item" onClick={handlePhoneClick}>
                      <div className="viator-contact-icon" style={{ backgroundColor: `${companyInfo.themeColor}15` }}>
                        <i className="ri-phone-line" style={{ color: companyInfo.themeColor }}></i>
                      </div>
                      <div className="viator-contact-details">
                        <span className="viator-contact-label">Phone Support</span>
                        <span className="viator-contact-value">{companyInfo.phone}</span>
                      </div>
                    </div>
                  </div>

                  <p className="viator-contact-note">
                    Our support team is available 24/7 to assist you with any questions
                  </p>
                </div>
              </div>
            </section>
          )}

          {activeTab === 'suppliers' && (
            <section className="viator-suppliers-section">
              <div className="viator-container">
                {/* Section Header */}
                <div className="viator-suppliers-header">
                  <span className="viator-suppliers-badge">
                    <i className="ri-handshake-line"></i> OUR INTEGRATED SUPPLIERS
                  </span>
                  <h2 className="viator-section-title">
                    Connect With <span className="viator-accent">Leading Travel Suppliers</span>
                  </h2>
                  <p className="viator-section-subtitle">
                    Direct integrations with industry-leading travel suppliers worldwide
                  </p>
                </div>

                {/* Clickable Logo Grid */}
                <div className="viator-logo-grid">
                  {suppliers.map((supplier) => (
                    <div 
                      key={supplier.id} 
                      className="viator-logo-item viator-clickable"
                      onClick={() => handleSupplierClick(supplier.path)}
                      role="button"
                      tabIndex={0}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          handleSupplierClick(supplier.path);
                        }
                      }}
                    >
                      <div className="viator-logo-wrapper">
                        <img 
                          src={supplier.logo} 
                          alt={supplier.name}
                          className="viator-supplier-logo"
                          onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = `https://via.placeholder.com/80x40?text=${supplier.name.charAt(0)}`;
                          }}
                        />
                      </div>
                      <span className="viator-logo-name">{supplier.name}</span>
                      <span className="viator-view-details-hint">
                        View Details <i className="ri-arrow-right-line"></i>
                      </span>
                    </div>
                  ))}
                </div>

                {/* Simple Stats */}
                <div className="viator-supplier-stats-simple">
                 
                  <div className="viator-stat-simple">
                    <span className="viator-stat-number">1M+</span>
                    <span className="viator-stat-label">Hotel Properties</span>
                  </div>
                  <div className="viator-stat-simple">
                    <span className="viator-stat-number">80+</span>
                    <span className="viator-stat-label">Airlines</span>
                  </div>
                  <div className="viator-stat-simple">
                    <span className="viator-stat-number">300K+</span>
                    <span className="viator-stat-label">Activities</span>
                  </div>
                </div>
              </div>
            </section>
          )}

          {activeTab === 'pricing' && (
            <section className="viator-pricing-section">
              <div className="viator-container">
                <div className="viator-pricing-header">
                  <h2 className="viator-section-title viator-text-center">
                    Plans & <span className="viator-accent">Pricing</span>
                  </h2>
                  <p className="viator-section-subtitle">
                    Pick the plan that's right for your business growth
                  </p>
                  
                  {/* Billing Toggle */}
                  <div className="viator-billing-toggle">
                    <button
                      className={`viator-toggle-btn ${billingCycle === 1 ? 'viator-toggle-active' : ''}`}
                      onClick={() => setBillingCycle(1)}
                    >
                      Monthly
                    </button>
                    <button
                      className={`viator-toggle-btn ${billingCycle === 2 ? 'viator-toggle-active' : ''}`}
                      onClick={() => setBillingCycle(2)}
                    >
                      Annually
                      <span className="viator-save-badge">Save 20%</span>
                    </button>
                  </div>
                </div>

                {/* Pricing Cards */}
                <div className="viator-pricing-grid">
                  {plans.map((plan) => {
                    const isMonthly = billingCycle === 1;
                    const accentColor = getAccentColor(plan.name);
                    
                    return (
                      <div key={plan.id} className="viator-pricing-card-enhanced">
                        {plan.popular && (
                          <div className="viator-popular-badge">
                            ⭐ MOST POPULAR
                          </div>
                        )}

                        <div className="viator-pricing-card-content">
                          {/* Pricing Side */}
                          <div className="viator-pricing-left" style={{ backgroundColor: plan.popular ? '#F4F9FF' : '#ffffff' }}>
                            <div className="viator-pricing-emoji">{plan.emoji}</div>
                            <h3 className="viator-pricing-name">{plan.name}</h3>
                            <p className="viator-pricing-description">{plan.description}</p>

                            <div className="viator-pricing-amount">
                              <span className="viator-currency">INR</span>
                              <span className="viator-price">
                                {isMonthly ? plan.monthly.toLocaleString() : plan.annually.toLocaleString()}
                              </span>
                              <span className="viator-pricing-period">
                                /{isMonthly ? 'month' : 'year'}
                              </span>
                            </div>

                            <button 
                              className="viator-pricing-cta"
                              style={{ backgroundColor: accentColor }}
                              onClick={() => window.location.href = '/signup'}
                            >
                              Start Free Trial <i className="ri-arrow-right-line"></i>
                            </button>

                            {/* Quick 3-Step Setup */}
                            <div className="viator-quick-setup">
                              <h4 className="viator-setup-title">Quick 3-Step Setup</h4>
                              <div className="viator-setup-steps">
                                <div className="viator-setup-step">
                                  <div className="viator-step-marker" style={{ background: accentColor }}>1</div>
                                  <div className="viator-step-content">
                                    <strong>Sign Up & Choose Plan</strong>
                                    <span>Create your account in 30 seconds - no credit card required</span>
                                  </div>
                                </div>
                                <div className="viator-setup-step">
                                  <div className="viator-step-marker" style={{ background: accentColor }}>2</div>
                                  <div className="viator-step-content">
                                    <strong>Select Your Suppliers</strong>
                                    <span>Choose from leading travel suppliers with one click</span>
                                  </div>
                                </div>
                                <div className="viator-setup-step">
                                  <div className="viator-step-marker" style={{ background: accentColor }}>3</div>
                                  <div className="viator-step-content">
                                    <strong>Launch Your Portal</strong>
                                    <span>Go live with your branded website in minutes</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          {/* Features Side */}
                          <div className="viator-pricing-right">
                            <h5 className="viator-features-title" style={{ color: accentColor }}>
                              Included in {plan.name}
                            </h5>
                            
                            <div className="viator-features-list">
                              {plan.features.map((feature, index) => (
                                <div key={index} className="viator-feature-item">
                                  <div className="viator-feature-check" style={{ backgroundColor: `${accentColor}15` }}>
                                    <i className="ri-check-line" style={{ color: accentColor }}></i>
                                  </div>
                                  <span>{feature}</span>
                                </div>
                              ))}
                            </div>

                            <h5 className="viator-features-title viator-addons-title" style={{ color: accentColor }}>
                              Add-Ons
                            </h5>
                            
                            <div className="viator-addons-list">
                              {plan.addons.map((addon, index) => (
                                <div key={index} className="viator-addon-item">
                                  <div className="viator-addon-check" style={{ backgroundColor: `${accentColor}15` }}>
                                    <i className="ri-add-line" style={{ color: accentColor }}></i>
                                  </div>
                                  <span>{addon}</span>
                                </div>
                              ))}
                            </div>

                            <div className="viator-pricing-footer">
                              <span>✓ Transparent pricing, no hidden fees</span>
                              <span className="viator-support-link" style={{ color: accentColor }}>
                                Need help? Chat with us
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
          )}
        </div>
      </div>

      {/* Styles with viator- prefix */}
      <style jsx>{`
        .viator-supplier-container {
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
          line-height: 1.6;
          color: #1e293b;
        }

        .viator-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 24px;
        }

        /* Header Styles */
        .viator-header {
          background: #ffffff;
          color: #1e293b;
          padding: 80px 0 40px;
          text-align: center;
        }

        .viator-title {
          font-size: 3.5rem;
          font-weight: 800;
          margin: 0 0 15px;
          line-height: 1.2;
          color: #1e293b;
        }

        .viator-highlight {
          font-size: 4rem;
          display: inline-block;
          font-weight: 800;
        }

        .viator-subtitle {
          font-size: 1.25rem;
          max-width: 700px;
          margin: 0 auto;
          color: #475569;
        }

        /* Tabs Navigation */
        .viator-tabs-container {
          background: #ffffff;
          border-bottom: 2px solid #e2e8f0;
          margin-bottom: 40px;
          position: sticky;
          top: 0;
          z-index: 100;
          backdrop-filter: blur(10px);
          background: rgba(255, 255, 255, 0.95);
        }

        .viator-tabs {
          display: flex;
          gap: 30px;
          overflow-x: auto;
          padding: 0 24px;
        }

        .viator-tab {
          background: none;
          border: none;
          padding: 16px 0;
          font-size: 1rem;
          font-weight: 600;
          color: #64748b;
          cursor: pointer;
          position: relative;
          transition: all 0.2s;
          display: flex;
          align-items: center;
          gap: 8px;
          white-space: nowrap;
        }

        .viator-tab:hover {
          color: #0f172a;
        }

        .viator-tab-active {
          color: #0f172a;
        }

        .viator-tab-active::after {
          content: '';
          position: absolute;
          bottom: -2px;
          left: 0;
          right: 0;
          height: 3px;
          background: ${viatorData.themeColor};
        }

        .viator-tab-badge {
          background: ${viatorData.themeColor};
          color: white;
          font-size: 0.7rem;
          padding: 2px 8px;
          border-radius: 12px;
          font-weight: 500;
        }

        /* About Section */
        .viator-about {
          padding: 40px 0 60px;
          background: #ffffff;
        }

        .viator-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 50px;
          align-items: start;
        }

        .viator-section-title {
          font-size: 2.5rem;
          font-weight: 700;
          margin-bottom: 24px;
          color: #0f172a;
        }

        .viator-text-center {
          text-align: center;
        }

        .viator-accent {
          position: relative;
          display: inline-block;
        }

        .viator-accent::after {
          content: '';
          position: absolute;
          bottom: -4px;
          left: 0;
          width: 100%;
          height: 4px;
          background: ${viatorData.themeColor};
          border-radius: 2px;
        }

        .viator-text {
          font-size: 1.1rem;
          color: #475569;
          margin-bottom: 20px;
          line-height: 1.8;
        }

        .viator-features {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 20px;
          margin-top: 30px;
        }

        .viator-feature {
          display: flex;
          gap: 15px;
          padding: 20px;
          background: #f8fafc;
          border-radius: 12px;
          border: 1px solid #e2e8f0;
          transition: all 0.3s ease;
        }

        .viator-feature:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.05);
          border-color: ${viatorData.themeColor};
        }

        .viator-feature-icon {
          font-size: 2rem;
          min-width: 50px;
        }

        .viator-feature h4 {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 5px;
          color: #0f172a;
        }

        .viator-feature p {
          font-size: 0.95rem;
          color: #475569;
          margin: 0;
        }

        .viator-card {
          background: white;
          padding: 40px;
          border-radius: 24px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.08);
          border: 1px solid #eef2f6;
        }

        .viator-card-title {
          font-size: 1.5rem;
          font-weight: 600;
          margin-bottom: 24px;
          color: #0f172a;
        }

        .viator-mt-4 {
          margin-top: 30px;
        }

        .viator-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .viator-list-item {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 16px;
          font-size: 1rem;
          color: #334155;
        }

        .viator-check {
          font-weight: bold;
          font-size: 1.2rem;
        }

        .viator-certification-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
        }

        .viator-cert-tag {
          background: #f1f5f9;
          color: #334155;
          padding: 6px 12px;
          border-radius: 20px;
          font-size: 0.85rem;
          font-weight: 500;
          border: 1px solid #e2e8f0;
        }

        /* Services Section */
        .viator-services {
          padding: 40px 0 60px;
          background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
        }

        .viator-section-subtitle {
          font-size: 1.25rem;
          color: #475569;
          text-align: center;
          max-width: 700px;
          margin: 0 auto 50px;
        }

        .viator-services-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 30px;
        }

        .viator-service-card {
          background: white;
          padding: 35px 25px;
          border-radius: 20px;
          box-shadow: 0 10px 30px rgba(0,0,0,0.05);
          transition: all 0.3s ease;
          border: 1px solid #eef2f6;
          position: relative;
          overflow: hidden;
        }

        .viator-service-card::before {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: ${viatorData.themeColor};
          transform: scaleX(0);
          transition: transform 0.3s ease;
        }

        .viator-service-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 20px 40px rgba(227, 27, 35, 0.1);
        }

        .viator-service-card:hover::before {
          transform: scaleX(1);
        }

        .viator-service-icon {
          font-size: 3rem;
          margin-bottom: 20px;
        }

        .viator-service-title {
          font-size: 1.3rem;
          font-weight: 600;
          margin-bottom: 15px;
          color: #0f172a;
        }

        .viator-service-description {
          color: #475569;
          margin-bottom: 20px;
          line-height: 1.6;
        }

        .viator-service-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .viator-service-list-item {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 10px;
          color: #475569;
          font-size: 0.95rem;
        }

        .viator-dot {
          font-weight: bold;
        }

        /* Technology Section */
        .viator-technology {
          padding: 40px 0 60px;
          background: white;
        }

        .viator-integration-box,
        .viator-benefits-box {
          background: linear-gradient(135deg, #f8fafc, #f1f5f9);
          padding: 40px;
          border-radius: 24px;
          border: 1px solid #eef2f6;
        }

        .viator-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 12px;
          margin-top: 25px;
        }

        .viator-tag {
          background: white;
          padding: 8px 16px;
          border-radius: 30px;
          font-size: 0.9rem;
          color: ${viatorData.themeColor};
          font-weight: 500;
          border: 1px solid ${viatorData.themeColor}30;
          transition: all 0.3s ease;
        }

        .viator-tag:hover {
          background: ${viatorData.themeColor};
          color: white;
          border-color: ${viatorData.themeColor};
          transform: scale(1.05);
        }

        .viator-benefits-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }

        .viator-benefits-item {
          display: flex;
          align-items: center;
          gap: 12px;
          margin-bottom: 20px;
          font-size: 1.1rem;
          color: #1e293b;
        }

        .viator-benefits-icon {
          font-size: 1.5rem;
          background: white;
          width: 45px;
          height: 45px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 12px;
          box-shadow: 0 4px 10px rgba(0,0,0,0.05);
        }

        /* Why Zotrai Section */
        .viator-why-zotrai-section {
          padding: 40px 0 80px;
          background: #ffffff;
        }

        .viator-why-header {
          text-align: center;
          margin-bottom: 50px;
        }

        .viator-why-badge {
          display: inline-block;
          padding: 8px 20px;
          border-radius: 30px;
          font-weight: 600;
          font-size: 0.9rem;
          margin-bottom: 20px;
        }

        .viator-benefits-grid-full {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 25px;
          margin-bottom: 50px;
        }

        .viator-benefit-card {
          display: flex;
          gap: 20px;
          background: #f8fafc;
          padding: 25px;
          border-radius: 20px;
          border: 1px solid #eef2f6;
          transition: all 0.3s ease;
        }

        .viator-benefit-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px rgba(0,0,0,0.05);
          border-color: ${companyInfo.themeColor};
        }

        .viator-benefit-icon-wrapper {
          width: 60px;
          height: 60px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .viator-benefit-icon-wrapper i {
          font-size: 2rem;
        }

        .viator-benefit-content h3 {
          font-size: 1.2rem;
          font-weight: 600;
          margin-bottom: 8px;
          color: #0f172a;
        }

        .viator-benefit-content p {
          color: #475569;
          font-size: 0.95rem;
          margin-bottom: 12px;
          line-height: 1.6;
        }

        .viator-benefit-stat {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .viator-stat-highlight {
          font-size: 1.2rem;
          font-weight: 700;
        }

        .viator-integration-process {
          text-align: center;
          margin: 60px 0;
        }

        .viator-process-title {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 40px;
          color: #0f172a;
        }

        .viator-process-steps {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 30px;
        }

        .viator-process-step {
          text-align: center;
          padding: 30px;
          background: #f8fafc;
          border-radius: 20px;
          border: 1px solid #eef2f6;
        }

        .viator-process-step .viator-step-number {
          width: 50px;
          height: 50px;
          border-radius: 50%;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 1.5rem;
          font-weight: 700;
          margin: 0 auto 20px;
        }

        /* Contact Section */
        .viator-contact-section {
          background: #ffffff;
          padding: 60px;
          border-radius: 30px;
          text-align: center;
          color: #0f172a;
          margin-top: 60px;
          border: 2px solid #eef2f6;
          box-shadow: 0 10px 30px rgba(0,0,0,0.05);
        }

        .viator-contact-section h3 {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 15px;
          color: #0f172a;
        }

        .viator-contact-section p {
          font-size: 1.2rem;
          color: #475569;
          margin-bottom: 40px;
        }

        .viator-contact-info {
          display: flex;
          justify-content: center;
          gap: 30px;
          margin-bottom: 30px;
          flex-wrap: wrap;
        }

        .viator-contact-item {
          display: flex;
          align-items: center;
          gap: 15px;
          padding: 20px 30px;
          background: #f8fafc;
          border-radius: 16px;
          border: 1px solid #eef2f6;
          cursor: pointer;
          transition: all 0.3s ease;
          min-width: 280px;
        }

        .viator-contact-item:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 25px rgba(0,0,0,0.1);
          border-color: ${companyInfo.themeColor};
        }

        .viator-contact-icon {
          width: 50px;
          height: 50px;
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }

        .viator-contact-icon i {
          font-size: 1.8rem;
        }

        .viator-contact-details {
          display: flex;
          flex-direction: column;
          align-items: flex-start;
        }

        .viator-contact-label {
          font-size: 0.85rem;
          color: #64748b;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .viator-contact-value {
          font-size: 1.2rem;
          font-weight: 600;
          color: #0f172a;
        }

        .viator-contact-note {
          font-size: 0.95rem !important;
          color: #64748b !important;
          margin-top: 20px;
          margin-bottom: 0 !important;
        }

        /* Suppliers Section */
        .viator-suppliers-section {
          padding: 40px 0 80px;
          background: #ffffff;
        }

        .viator-suppliers-header {
          text-align: center;
          margin-bottom: 50px;
        }

        .viator-suppliers-badge {
          display: inline-block;
          background: linear-gradient(135deg, #2563eb15, #7c3aed15);
          color: #2563eb;
          padding: 8px 20px;
          border-radius: 30px;
          font-weight: 600;
          font-size: 0.9rem;
          margin-bottom: 20px;
          border: 1px solid #2563eb20;
        }

        .viator-suppliers-badge i {
          margin-right: 8px;
        }

        .viator-logo-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 30px;
          margin-bottom: 50px;
        }

        .viator-logo-item {
          text-align: center;
          padding: 20px;
          background: white;
          border-radius: 16px;
          border: 1px solid #eef2f6;
          transition: all 0.3s ease;
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          cursor: pointer;
          position: relative;
          overflow: hidden;
        }

        .viator-logo-item:hover {
          transform: translateY(-5px);
          box-shadow: 0 15px 30px rgba(37, 99, 235, 0.15);
          border-color: #2563eb;
        }

        .viator-logo-item:hover .viator-view-details-hint {
          opacity: 1;
          transform: translateY(0);
        }

        .viator-logo-wrapper {
          height: 80px;
          display: flex;
          align-items: center;
          justify-content: center;
          margin-bottom: 15px;
        }

        .viator-supplier-logo {
          max-width: 120px;
          max-height: 60px;
          object-fit: contain;
          transition: transform 0.3s ease;
        }

        .viator-logo-item:hover .viator-supplier-logo {
          transform: scale(1.05);
        }

        .viator-logo-name {
          font-size: 0.95rem;
          font-weight: 600;
          color: #1e293b;
          display: block;
          margin-bottom: 5px;
        }

        .viator-view-details-hint {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          font-size: 0.8rem;
          color: #2563eb;
          font-weight: 500;
          opacity: 0;
          transform: translateY(10px);
          transition: all 0.3s ease;
        }

        .viator-supplier-stats-simple {
          display: flex;
          justify-content: space-around;
          align-items: center;
          background: linear-gradient(135deg, #f8fafc, #ffffff);
          padding: 30px;
          border-radius: 20px;
          border: 1px solid #eef2f6;
          flex-wrap: wrap;
          gap: 20px;
        }

        .viator-stat-simple {
          text-align: center;
        }

        .viator-stat-simple .viator-stat-number {
          display: block;
          font-size: 2rem;
          font-weight: 800;
          color: #2563eb;
          line-height: 1.2;
        }

        .viator-stat-simple .viator-stat-label {
          font-size: 0.9rem;
          color: #64748b;
        }

        /* Pricing Section */
        .viator-pricing-section {
          padding: 40px 0 80px;
          background: linear-gradient(135deg, #f8fafc 0%, #ffffff 100%);
        }

        .viator-pricing-header {
          text-align: center;
          margin-bottom: 40px;
        }

        .viator-billing-toggle {
          display: inline-flex;
          background: white;
          padding: 4px;
          border-radius: 50px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 4px 12px rgba(0,0,0,0.05);
          margin-top: 20px;
        }

        .viator-toggle-btn {
          border: none;
          padding: 12px 32px;
          font-size: 1rem;
          font-weight: 600;
          border-radius: 50px;
          cursor: pointer;
          transition: all 0.3s ease;
          background: transparent;
          color: #64748b;
          position: relative;
        }

        .viator-toggle-active {
          background: #2563eb;
          color: white;
        }

        .viator-save-badge {
          background: #fbbf24;
          color: #1e293b;
          font-size: 0.7rem;
          padding: 2px 8px;
          border-radius: 12px;
          margin-left: 8px;
          font-weight: 500;
        }

        .viator-pricing-grid {
          max-width: 1000px;
          margin: 0 auto;
        }

        .viator-pricing-card-enhanced {
          background: white;
          border-radius: 24px;
          box-shadow: 0 20px 40px rgba(0,0,0,0.08);
          border: 1px solid #eef2f6;
          position: relative;
          overflow: hidden;
          transition: all 0.3s ease;
        }

        .viator-pricing-card-enhanced:hover {
          transform: translateY(-5px);
          box-shadow: 0 30px 60px rgba(37, 99, 235, 0.12);
        }

        .viator-popular-badge {
          position: absolute;
          top: 20px;
          right: 30px;
          background: #2563eb;
          color: white;
          padding: 8px 16px;
          border-radius: 30px;
          font-weight: 600;
          font-size: 0.85rem;
          z-index: 10;
          box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
        }

        .viator-pricing-card-content {
          display: grid;
          grid-template-columns: 1fr 1.2fr;
        }

        .viator-pricing-left {
          padding: 40px;
          border-right: 1px solid #eef2f6;
        }

        .viator-pricing-emoji {
          font-size: 2.5rem;
          margin-bottom: 15px;
        }

        .viator-pricing-name {
          font-size: 2rem;
          font-weight: 700;
          margin-bottom: 10px;
          color: #0f172a;
        }

        .viator-pricing-description {
          color: #475569;
          font-size: 0.95rem;
          margin-bottom: 25px;
        }

        .viator-pricing-amount {
          margin-bottom: 25px;
        }

        .viator-currency {
          font-size: 1.2rem;
          font-weight: 600;
          color: #64748b;
          margin-right: 5px;
        }

        .viator-price {
          font-size: 3.5rem;
          font-weight: 800;
          color: #0f172a;
          line-height: 1;
        }

        .viator-pricing-period {
          font-size: 1rem;
          color: #64748b;
          margin-left: 5px;
        }

        .viator-pricing-cta {
          width: 100%;
          border: none;
          padding: 16px;
          border-radius: 12px;
          color: white;
          font-weight: 600;
          font-size: 1.1rem;
          cursor: pointer;
          transition: all 0.3s ease;
          margin-bottom: 30px;
        }

        .viator-pricing-cta:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(0,0,0,0.1);
        }

        .viator-quick-setup {
          background: #f8fafc;
          border-radius: 16px;
          padding: 20px;
        }

        .viator-setup-title {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 20px;
          color: #0f172a;
        }

        .viator-setup-steps {
          display: flex;
          flex-direction: column;
          gap: 15px;
        }

        .viator-setup-step {
          display: flex;
          gap: 12px;
          align-items: flex-start;
        }

        .viator-step-marker {
          width: 28px;
          height: 28px;
          border-radius: 50%;
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 600;
          font-size: 0.9rem;
          flex-shrink: 0;
        }

        .viator-step-content {
          display: flex;
          flex-direction: column;
        }

        .viator-step-content strong {
          font-size: 0.95rem;
          margin-bottom: 4px;
        }

        .viator-step-content span {
          font-size: 0.85rem;
          color: #64748b;
        }

        .viator-pricing-right {
          padding: 40px;
          background: #ffffff;
        }

        .viator-features-title {
          font-size: 1.1rem;
          font-weight: 600;
          margin-bottom: 20px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .viator-addons-title {
          margin-top: 30px;
        }

        .viator-features-list,
        .viator-addons-list {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }

        .viator-feature-item,
        .viator-addon-item {
          display: flex;
          gap: 12px;
          align-items: flex-start;
        }

        .viator-feature-check,
        .viator-addon-check {
          width: 22px;
          height: 22px;
          border-radius: 6px;
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
          margin-top: 2px;
        }

        .viator-feature-check i,
        .viator-addon-check i {
          font-size: 14px;
        }

        .viator-feature-item span,
        .viator-addon-item span {
          font-size: 0.95rem;
          color: #334155;
          line-height: 1.5;
        }

        .viator-pricing-footer {
          margin-top: 30px;
          padding-top: 20px;
          border-top: 1px solid #e2e8f0;
          display: flex;
          justify-content: space-between;
          align-items: center;
          font-size: 0.9rem;
        }

        .viator-support-link {
          cursor: pointer;
          font-weight: 500;
        }

        .viator-support-link:hover {
          text-decoration: underline;
        }

        /* Accessibility */
        .viator-clickable:focus-visible {
          outline: 3px solid #2563eb;
          outline-offset: 2px;
        }

        /* Responsive Design */
        @media (max-width: 1024px) {
          .viator-grid {
            grid-template-columns: 1fr;
            gap: 30px;
          }
          
          .viator-services-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .viator-benefits-grid-full {
            grid-template-columns: 1fr;
          }

          .viator-process-steps {
            grid-template-columns: 1fr;
          }

          .viator-logo-grid {
            grid-template-columns: repeat(3, 1fr);
          }

          .viator-pricing-card-content {
            grid-template-columns: 1fr;
          }

          .viator-pricing-left {
            border-right: none;
            border-bottom: 1px solid #eef2f6;
          }

          .viator-contact-info {
            flex-direction: column;
            align-items: center;
          }

          .viator-contact-item {
            width: 100%;
            max-width: 400px;
          }
        }

        @media (max-width: 768px) {
          .viator-title {
            font-size: 2.5rem;
          }
          
          .viator-highlight {
            font-size: 3rem;
          }
          
          .viator-tabs {
            gap: 20px;
            overflow-x: auto;
            padding-bottom: 5px;
          }
          
          .viator-tab {
            font-size: 0.95rem;
            white-space: nowrap;
          }
          
          .viator-services-grid {
            grid-template-columns: 1fr;
          }
          
          .viator-section-title {
            font-size: 2rem;
          }

          .viator-features {
            grid-template-columns: 1fr;
          }

          .viator-logo-grid {
            grid-template-columns: repeat(2, 1fr);
          }

          .viator-supplier-stats-simple {
            flex-direction: column;
            gap: 20px;
          }

          .viator-contact-section {
            padding: 40px 20px;
          }

          .viator-contact-section h3 {
            font-size: 1.8rem;
          }

          .viator-contact-item {
            padding: 15px 20px;
          }

          .viator-contact-value {
            font-size: 1rem;
          }
        }

        @media (max-width: 480px) {
          .viator-header {
            padding: 60px 0 30px;
          }

          .viator-logo-grid {
            grid-template-columns: 1fr;
          }

          .viator-pricing-left,
          .viator-pricing-right {
            padding: 25px;
          }

          .viator-price {
            font-size: 2.5rem;
          }

          .viator-billing-toggle {
            flex-direction: column;
            width: 100%;
            border-radius: 12px;
          }

          .viator-toggle-btn {
            width: 100%;
          }

          .viator-contact-item {
            flex-direction: column;
            text-align: center;
          }

          .viator-contact-details {
            align-items: center;
          }
        }
      `}</style>
    </>
  );
};

// Simple Helmet component for meta tags
const Helmet = ({ children }) => {
  return <>{children}</>;
};

export default ViatorSupplierHighlight;